'use client';

import { useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import { useSwiperSlide } from 'swiper-8/react';

import { type Masked, unmask } from '~/contentful/graphql';
import { flattenCollection } from '~/contentful/utils/collection';
import { Image } from '~/ui/modules/image';
import { Breakpoint } from '~/ui/styles/grid';
import typographyStyles from '~/ui/styles/typography.module.scss';
import { type AspectRatio } from '~/ui/utils/aspectRatio';
import { Carousel } from '~/v1/components/carousel/carousel';

import {
  type SpotlightSlideshowSlide_AssetFragment,
  type SpotlightSlideshow_SpotlightSlideshowFragment,
} from './query';
import styles from './slideshow.module.scss';

export interface SpotlightSlideshowProps {
  data: Masked<typeof SpotlightSlideshow_SpotlightSlideshowFragment>;
  isOpen: boolean;
}

export function SpotlightSlideshow({
  data,
  isOpen = false,
}: SpotlightSlideshowProps): React.ReactElement {
  const { imagesCollection } = unmask<typeof SpotlightSlideshow_SpotlightSlideshowFragment>(data);
  const images = flattenCollection(imagesCollection);

  return (
    <div className={styles.slideshow}>
      <Carousel
        slidesPerView={1}
        className={styles.swiper}
        slideClasses={styles.slide}
        buttonClasses={cx(styles.navigationButtons, 'dark-background')}
        pagination={{
          type: 'fraction',
        }}
        spaceBetween={32}
        isTransparent={true}
      >
        {images.map((image, index) => {
          return <Slide data={image} isOpen={isOpen} key={index} />;
        })}
      </Carousel>
    </div>
  );
}

function Slide({
  data,
  isOpen,
}: {
  data: Masked<typeof SpotlightSlideshowSlide_AssetFragment>;
  isOpen: boolean;
}) {
  const image = unmask<typeof SpotlightSlideshowSlide_AssetFragment>(data);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>();
  const [isLineClamped, setIsLineClamped] = useState<boolean>(false);
  const [isCaptionExpanded, setIsCaptionExpanded] = useState<boolean>(false);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const captionRef = useRef<HTMLDivElement>(null);
  const expandCaptionRef = useRef<HTMLButtonElement>(null);

  const swiperSlide = useSwiperSlide();

  // reset caption state when carousel closes
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    setIsCaptionExpanded(false);
  }

  const checkIsLineClamped = () => {
    setTimeout(() => {
      const clientHeight = captionRef.current?.clientHeight || 0;
      const scrollHeight = captionRef.current?.scrollHeight || 0;

      setIsLineClamped(clientHeight < scrollHeight);
    }, 0);
  };

  useEffect(() => {
    checkIsLineClamped();
  }, [isOpen]);

  useEffect(() => {
    const onResize = () => {
      checkIsLineClamped();
    };
    // onResize should run once on mount
    onResize();

    const onDocumentClick = (event: PointerEvent) => {
      if (
        isCaptionExpanded &&
        swiperSlide.isActive &&
        !captionRef.current?.contains(event.target as Node) &&
        !expandCaptionRef.current?.contains(event.target as Node)
      ) {
        setIsCaptionExpanded(false);
      }
    };
    document.addEventListener('click', onDocumentClick);

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('click', onDocumentClick);
    };
  }, [isCaptionExpanded, swiperSlide.isActive]);

  const handleCaptionExpandClicked = () => {
    setIsCaptionExpanded(true);
  };

  return (
    <div
      className={cx(styles.content, {
        [styles.isActive]: swiperSlide.isActive,
      })}
      style={{
        aspectRatio,
      }}
    >
      <Image
        onCalculateAspectRatio={setAspectRatio}
        image={image}
        className={styles.image}
        colSpan={{
          [Breakpoint.MOBILE]: 5,
          [Breakpoint.TABLET]: 7,
          [Breakpoint.DESKTOP]: 9,
        }}
      />

      {image.description && (
        <div
          className={cx(styles.descriptionWrapper, {
            [styles.isCaptionExpanded]: isCaptionExpanded,
          })}
        >
          <div
            className={cx(typographyStyles.caption, styles.caption, {
              [styles.isLineClamped]: isLineClamped,
            })}
            aria-label={image.description}
            ref={captionRef}
          >
            {image.description}
            <button
              className={cx(typographyStyles.caption, styles.more, {
                [styles.showMore]: isLineClamped && !isCaptionExpanded,
              })}
              onClick={handleCaptionExpandClicked}
              ref={expandCaptionRef}
            >
              ...More
            </button>
          </div>
        </div>
      )}
      <div className={styles.shroud} aria-hidden></div>
    </div>
  );
}
